import React, { useState, useRef, useEffect } from 'react';
import SimpleDraggable from './SimpleDraggable';

interface TextParagraph {
  children: { text: string }[];
  type: string;
}

interface Block {
  uuidkey: string;
  contentType: 'text' | 'gif' | 'img';
  dataUrl?: string;
  left: number;
  top: number;
  width: number;
  height: number;
  rotateAngle: number;
  textContent?: TextParagraph[];
}

interface MoodboardBlockProps {
  block: Block;
  isFocused: boolean;
  onUpdate: (updates: Partial<Block>) => void;
  onFocus: () => void;
}

const MoodboardBlock: React.FC<MoodboardBlockProps> = ({
  block,
  isFocused,
  onUpdate,
  onFocus
}) => {
  const [isEditing, setIsEditing] = useState(false);
  // Helper functions to convert between rich text and plain text
  const textContentToString = (textContent?: TextParagraph[]) => {
    if (!textContent) return '';
    return textContent.map(paragraph =>
      paragraph.children.map(child => child.text).join('')
    ).join('\n');
  };

  const stringToTextContent = (text: string): TextParagraph[] => {
    if (!text.trim()) {
      return [{ children: [{ text: 'Double-click to edit' }], type: 'paragraph' }];
    }
    const lines = text.split('\n');
    return lines.map(line => ({
      children: [{ text: line || '' }],
      type: 'paragraph'
    }));
  };

  const [tempText, setTempText] = useState(textContentToString(block.textContent));
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleDrag = (e: MouseEvent | TouchEvent, data: { x: number; y: number; deltaX: number; deltaY: number }) => {
    onUpdate({
      left: data.x,
      top: data.y
    });
  };

  const handleDoubleClick = () => {
    if (block.contentType === 'text') {
      setIsEditing(true);
      setTempText(textContentToString(block.textContent));
    }
  };

  const handleTextSubmit = () => {
    const newTextContent = stringToTextContent(tempText);
    console.log('Garden App - Saving text content:', newTextContent);
    onUpdate({ textContent: newTextContent });
    setIsEditing(false);
  };

  const handleTextCancel = () => {
    setTempText(textContentToString(block.textContent));
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    } else if (e.key === 'Escape') {
      handleTextCancel();
    }
  };

  // Focus textarea when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDragging) {
      onFocus();
    }
  };

  const blockStyle: React.CSSProperties = {
    width: block.width,
    height: block.height,
    transform: `rotate(${block.rotateAngle}deg)`,
    cursor: isDragging ? 'grabbing' : 'grab',
    border: isFocused ? '2px solid #007AFF' : '1px solid transparent',
    borderRadius: '0',
    zIndex: isFocused ? 1000 : 1
  };

  return (
    <SimpleDraggable
      position={{ x: block.left, y: block.top }}
      onDrag={handleDrag}
      onStart={() => setIsDragging(true)}
      onStop={() => setIsDragging(false)}
      handle=".drag-handle"
    >
      <div
        className={`moodboard-block ${block.contentType}-block`}
        style={blockStyle}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <div className="drag-handle" style={{ width: '100%', height: '100%' }}>
          {block.contentType === 'text' && (
            <>
              {isEditing ? (
                <textarea
                  ref={textareaRef}
                  value={tempText}
                  onChange={(e) => setTempText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onBlur={handleTextSubmit}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    background: 'transparent',
                    fontFamily: "'geneva', 'Courier New', monospace",
                    fontSize: '20px',
                    padding: '10px',
                    overflow: 'hidden',
                    lineHeight: '1.4'
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    padding: '10px',
                    fontSize: '20px',
                    lineHeight: '1.4',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    background: 'transparent',
                    borderRadius: '0',
                    fontFamily: "'geneva', 'Courier New', monospace"
                  }}
                >
                  {textContentToString(block.textContent) || 'Double-click to edit'}
                </div>
              )}
            </>
          )}

          {block.contentType === 'gif' && (
            <img
              src={block.dataUrl}
              alt="GIF"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '0'
              }}
              draggable={false}
            />
          )}

          {block.contentType === 'img' && (
            <img
              src={block.dataUrl}
              alt="Photo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '0'
              }}
              draggable={false}
            />
          )}
        </div>

        {/* Resize handles - only show when focused */}
        {isFocused && !isEditing && (
          <>
            <div
              className="resize-handle"
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '10px',
                height: '10px',
                background: '#007AFF',
                cursor: 'se-resize',
                touchAction: 'none'
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                // Simple resize implementation
                const startX = e.clientX;
                const startY = e.clientY;
                const startWidth = block.width;
                const startHeight = block.height;

                const handleMouseMove = (e: MouseEvent) => {
                  const newWidth = Math.max(50, startWidth + (e.clientX - startX));
                  const newHeight = Math.max(30, startHeight + (e.clientY - startY));
                  onUpdate({ width: newWidth, height: newHeight });
                };

                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                // Touch resize implementation
                const touch = e.touches[0];
                const startX = touch.clientX;
                const startY = touch.clientY;
                const startWidth = block.width;
                const startHeight = block.height;

                const handleTouchMove = (e: TouchEvent) => {
                  e.preventDefault();
                  const touch = e.touches[0];
                  const newWidth = Math.max(50, startWidth + (touch.clientX - startX));
                  const newHeight = Math.max(30, startHeight + (touch.clientY - startY));
                  onUpdate({ width: newWidth, height: newHeight });
                };

                const handleTouchEnd = () => {
                  document.removeEventListener('touchmove', handleTouchMove);
                  document.removeEventListener('touchend', handleTouchEnd);
                  document.removeEventListener('touchcancel', handleTouchEnd);
                };

                document.addEventListener('touchmove', handleTouchMove, { passive: false });
                document.addEventListener('touchend', handleTouchEnd);
                document.addEventListener('touchcancel', handleTouchEnd);
              }}
            />
          </>
        )}
      </div>
    </SimpleDraggable>
  );
};

export default MoodboardBlock;